import {
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { createStyles, makeStyles } from '@mui/styles';
import { useIssueOverlayControls } from '../DocumentationDataGrid/components/IssueOverlayControls';
import Controllers from 'components/common/gallery/controllers';
import { useProjectId } from 'store/project';
import { useNavigate } from 'react-router-dom';
import { useSelectedStoreContext } from 'components/common/withSelectedStore';
import { SelectableIssueCard } from './SelectableIssueCard';
import { useAppNavigation } from 'contexts/navigation';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      position: 'absolute',
      bottom: 0,
      overflowX: 'hidden',
      overflowY: 'visible',
      width: '100%',
      height: '130px',
      background: 'linear-gradient(#00000055, #000000f0)',
      zIndex: 1203,
      paddingBottom: '6px',
    },
    scrollableContainer: {
      padding: '2px calc(50% - 190px)', // 50% - half card size;
      position: 'absolute',
      display: 'flex',
      alignItems: 'flex-end',
      overflowX: 'scroll',
      pointerEvents: 'none',
      width: '100%',
      boxSizing: 'border-box',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
  })
);

function _IssueCards(
  {
    issues,
  }: {
    issues: IssueModel[];
  },
  ref: any
): ReactElement {
  const navigate = useNavigate();
  const cs = useStyles();
  const scrollableTarget = useRef<HTMLDivElement | null>(null);
  const projectId = useProjectId();
  const { goToIssueDetails } = useAppNavigation();

  const { issuesVisibleStore } = useIssueOverlayControls();
  const [_, setVisible] = useState(issuesVisibleStore.get());

  const { select, selectedStore } = useSelectedStoreContext<string>();
  const onClick = useCallback(
    (issueModel: IssueModel) => {
      const current = selectedStore.get();
      if (current === issueModel._id) {
        goToIssueDetails(projectId, issueModel._id);
      } else {
        select(issueModel._id);
      }
    },
    [selectedStore, goToIssueDetails, projectId, select]
  );

  useEffect(() => {
    return issuesVisibleStore.subscribe(() => {
      setVisible(issuesVisibleStore.get());
    });
  }, [issuesVisibleStore]);

  const issueCards = useMemo(() => {
    const cards = issues.map((issue) => (
      <SelectableIssueCard
        key={`${issue._id}`}
        issue={issue}
        onClick={onClick}
      />
    ));
    return cards;
  }, [issues, onClick]);

  const convertVerticalScroll = useCallback((evt) => {
    if (!scrollableTarget.current) {
      return;
    }
    scrollableTarget.current.scrollBy(evt.deltaY, 0);
  }, []);

  const scrollInDirection = useCallback((direction: number) => {
    if (
      !scrollableTarget.current?.childElementCount
    ) {
      return;
    }
    const card = scrollableTarget.current.firstElementChild!;
    scrollableTarget.current.scrollBy({
      left: direction * card.clientWidth,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        bottom: '0',
        overflowX: 'visible',
        overflowY: 'visible',
        width: '100%',
        background: 'linear-gradient(#00000055, #000000f0)',
        zIndex: 1203,
        scrollbarWidth: 'none',
        paddingBottom: '6px',
      }}
    >
      <div
        style={{
          position: 'relative',
          maxHeight: '130px',
          overflowX: 'visible',
          overflowY: 'visible',
          display: 'flex',
          alignItems: 'flex-end',
          width: '100%',
          scrollbarWidth: 'none',
          height: '130px',
        }}
        onWheelCapture={convertVerticalScroll}
      >
        <div ref={scrollableTarget} className={cs.scrollableContainer}>
          {issueCards}
        </div>
        <Controllers
          action={scrollInDirection}
          showControllers={issueCards.length > 1}
          opacity={0.5}
        />
      </div>
    </div>
  );
}

export const IssueCards = forwardRef(_IssueCards);
